import React, { useState } from 'react'
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from '@mui/material'
import { makeStyles } from '@material-ui/core'
import {
  ERROR_ON_STATE_AND_CITY,
  GENDER_LIST,
} from 'src/utils/constants/BuyPolicy'
import { COLORS } from 'src/utils/Themes/colors'
import IconButton from '@mui/material/IconButton'
import { Clear } from '@mui/icons-material'
import CustomDatePicker from 'src/components/BuyPolicy/common/CustomDatePicker'
import { getAddress } from 'src/services/buyPolicyService'
import { addScroll, removeScroll } from 'src/utils/commonMethods'
import { NOTIFICATION_MSG_REPORTS } from 'src/utils/constants/CustomerReports'

const ProposerDetailsForm = ({
  proposerDetails,
  setProposerDetails,
  providerDetails,
  formErrors = {},
}) => {
  const classes = useStyles()
  const { currentAddress = {}, permanentAddress = {} } =
    proposerDetails?.address ?? {}
  const [isPermanentAddress, setIsPermanentAddress] = useState(
    proposerDetails?.address?.isCurrentAddressPermanent
  )
  const [, setAlertMessage] = useState('')
  const [, setOpenAlert] = useState(false)

  const handleAddressCheck = (event) => {
    setIsPermanentAddress(event?.target?.checked)
    if (event?.target?.checked) {
      setProposerDetails({
        ...proposerDetails,
        address: {
          ...proposerDetails?.address,
          permanentAddress: {
            ...currentAddress,
          },
          isCurrentAddressPermanent: event?.target?.checked,
        },
      })
    } else {
      setProposerDetails({
        ...proposerDetails,
        address: {
          ...proposerDetails?.address,
          permanentAddress: {
            addressLine: '',
            landmark: '',
            pincode: '',
            city: '',
            state: '',
          },
          isCurrentAddressPermanent: event?.target?.checked,
        },
      })
    }
  }

  const handleProposerDetailsChange = (event) => {
    switch (event?.target?.name) {
      case 'panNumber':
      case 'aadharNumber':
        setProposerDetails({
          ...proposerDetails,
          [event?.target?.name]: event?.target?.value?.toUpperCase(),
        })
        break
      default:
        setProposerDetails({
          ...proposerDetails,
          [event?.target?.name]: event?.target?.value,
        })
        break
    }
  }

  const handleClear = (key) => {
    setProposerDetails({
      ...proposerDetails,
      [key]: '',
    })
  }

  const handleProposerAddressChange = (event, addressType) => {
    let updatedAddress = {
      ...proposerDetails?.address,
      [addressType]: {
        ...proposerDetails?.address?.[addressType],
        [event?.target?.name]: event?.target?.value,
      },
    }
    if (isPermanentAddress) {
      updatedAddress = {
        ...updatedAddress,
        permanentAddress: updatedAddress?.currentAddress,
      }
    }
    setProposerDetails({
      ...proposerDetails,
      address: {
        ...updatedAddress,
      },
    })
  }

  const isErrorFormField = (fieldName, addressType = null) => {
    let formError = formErrors?.[fieldName]
    if (formErrors?.address && addressType) {
      formError = formErrors?.address?.[addressType]?.[fieldName]
    }
    if (formError) {
      return formError?.error
    }

    return false
  }

  const fetchFullAddress = async (pincode, addressType) => {
    const response = await getAddress(pincode)
    try {
      if (response?.res?.status !== 200) {
        setAlertMessage(
          NOTIFICATION_MSG_REPORTS?.PLEASE_TRY_AGAIN_AFTER_SOMETIME
        )
        setOpenAlert(true)
      }
      // Success
      const responseData = response?.res?.data
      if (responseData) {
        const city = responseData?.[0]?.PostOffice?.[0]?.Division
        const state = responseData?.[0]?.PostOffice?.[0]?.State

        let updatedAddress = {
          ...proposerDetails?.address,
          [addressType]: {
            ...proposerDetails?.address?.[addressType],
            city,
            state,
            pincode,
          },
        }
        if (isPermanentAddress) {
          updatedAddress = {
            ...updatedAddress,
            permanentAddress: updatedAddress?.currentAddress,
          }
        }

        setProposerDetails({
          ...proposerDetails,
          address: {
            ...updatedAddress,
          },
        })
      } else {
        setOpenAlert(true)
        setAlertMessage(responseData?.message)
      }
    } catch (err) {
      setAlertMessage(ERROR_ON_STATE_AND_CITY)
      setOpenAlert(true)
    }
  }

  return (
    <div>
      <div className={classes.backgroundContainer}>
        <div className={classes.container}>
          <div className={classes.subHeadingContainer}>
            <span className={classes.subHeading}>Proposer Details</span>
          </div>
          <div className={classes.inputContainer}>
            <div className={classes.inputContent}>
              <TextField
                id='firstName'
                name='firstName'
                type='text'
                placeholder='First Name *'
                required
                error={isErrorFormField('firstName')}
                helperText={
                  isErrorFormField('firstName') ? 'Invalid First Name' : ''
                }
                value={proposerDetails?.firstName ?? ''}
                onChange={handleProposerDetailsChange}
                className={`${classes.inputTextField} ${classes.firstNameField}`}
              ></TextField>
            </div>

            <div className={classes.inputContent}>
              <TextField
                id='lastName'
                name='lastName'
                type='text'
                placeholder='Last Name *'
                required
                error={isErrorFormField('lastName')}
                helperText={
                  isErrorFormField('lastName') ? 'Invalid Last Name' : ''
                }
                value={proposerDetails?.lastName ?? ''}
                onChange={handleProposerDetailsChange}
                className={`${classes.inputTextField} ${classes.firstNameField}`}
              ></TextField>
            </div>

            <div className={classes.inputContent}>
              <CustomDatePicker
                id='dateOfBirth'
                name='dateOfBirth'
                type='date'
                placeholder='Date of birth *'
                error={isErrorFormField('dateOfBirth')}
                helperText={
                  isErrorFormField('dateOfBirth') ? 'Invalid Date of Birth' : ''
                }
                value={proposerDetails?.dateOfBirth ?? ''}
                onChange={handleProposerDetailsChange}
                className={`${classes.inputTextField} ${classes.firstNameField}`}
              ></CustomDatePicker>
            </div>
          </div>

          <div className={classes.inputContainer}>
            <div className={classes.inputContent}>
              <div className={classes.inputItem}>
                <FormControl>
                  <RadioGroup
                    row
                    id='gender'
                    name='gender'
                    error={isErrorFormField('gender')}
                    helperText={
                      isErrorFormField('gender') ? 'Invalid Gender' : ''
                    }
                    value={proposerDetails?.gender ?? ''}
                    onChange={handleProposerDetailsChange}
                  >
                    {GENDER_LIST?.map((genderListItem) => {
                      return (
                        <FormControlLabel
                          key={`gender_${genderListItem?.label}`}
                          value={genderListItem?.enum}
                          control={<Radio />}
                          label={genderListItem?.label}
                          className={classes.genterRadio}
                        />
                      )
                    })}
                  </RadioGroup>
                </FormControl>
              </div>
            </div>

            <div className={classes.inputContent}>
              <TextField
                id='height'
                type='number'
                name='height'
                placeholder='Height *'
                value={proposerDetails?.height ?? ''}
                required
                error={isErrorFormField('height')}
                helperText={isErrorFormField('height') ? 'Invalid Height' : ''}
                className={`${classes.inputTextField} ${classes.setFieldHeight}`}
                onChange={(event) => {
                  if (event?.target?.value > 0 || event?.target?.value === '') {
                    handleProposerDetailsChange(event)
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>CM</InputAdornment>
                  ),
                }}
              ></TextField>
            </div>

            <div className={classes.inputContent}>
              <TextField
                id='weight'
                type='number'
                name='weight'
                placeholder='Weight *'
                value={proposerDetails?.weight ?? ''}
                required
                error={isErrorFormField('weight')}
                helperText={isErrorFormField('weight') ? 'Invalid Weight' : ''}
                className={`${classes.inputTextField} ${classes.setFieldHeight}`}
                onChange={(event) => {
                  if (event?.target?.value > 0 || event?.target?.value === '') {
                    handleProposerDetailsChange(event)
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>KG</InputAdornment>
                  ),
                }}
              ></TextField>
            </div>
          </div>

          <div className={classes.inputContainer}>
            <div className={classes.inputContent}>
              <TextField
                id='email'
                name='email'
                type='email'
                placeholder='Email *'
                required
                error={isErrorFormField('email')}
                helperText={isErrorFormField('email') ? 'Invalid Email' : ''}
                value={proposerDetails?.email ?? ''}
                onChange={handleProposerDetailsChange}
                className={`${classes.inputTextField} ${classes.setFieldHeight}`}
              ></TextField>
            </div>

            <div className={classes.inputContent}>
              <TextField
                id='phone'
                name='phone'
                type='phone'
                placeholder='Phone *'
                error={isErrorFormField('phone')}
                helperText={
                  isErrorFormField('phone') ? 'Invalid Mobile number' : ''
                }
                value={proposerDetails?.phone ?? ''}
                onChange={handleProposerDetailsChange}
                className={`${classes.inputTextField} ${classes.setFieldHeight}`}
              ></TextField>
            </div>

            <div className={classes.inputContent}>
              <FormControl
                className={classes.professionContainer}
                error={isErrorFormField('profession')}
              >
                <Autocomplete
                  id='profession'
                  name='profession'
                  value={
                    providerDetails?.occupation?.find(
                      (item) => item?.value === proposerDetails?.profession
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleProposerDetailsChange({
                      target: {
                        name: 'profession',
                        value: newValue?.value || '',
                      },
                    })
                  }}
                  options={providerDetails?.occupation ?? []}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id='profession'
                      name='profession'
                      placeholder='Occupation'
                      className={classes.inputTextField}
                      sx={{
                        '& .MuiInputBase-root': {
                          '& input': {
                            height: '0.7rem !important',
                            minHeight: 'unset',
                          },
                        },
                        '& .MuiInputAdornment-root': {
                          display: proposerDetails?.profession ? '' : 'none',
                        },
                      }}
                      endAdornment={
                        proposerDetails?.profession && (
                          <IconButton
                            id='professionClear'
                            className='clear-button'
                            onClick={() => handleClear('profession')}
                          >
                            <Clear />
                          </IconButton>
                        )
                      }
                    />
                  )}
                  renderOption={(props, option) => (
                    <MenuItem {...props} key={option?.value} value={option}>
                      {option?.label}
                    </MenuItem>
                  )}
                  onOpen={() => {
                    removeScroll()
                  }}
                  onClose={() => {
                    addScroll()
                  }}
                />
                {isErrorFormField('profession') && (
                  <FormHelperText>Invalid Occupation</FormHelperText>
                )}
              </FormControl>
            </div>
          </div>

          <div className={classes.inputContainer}>
            <div className={classes.inputContent}>
              <FormControl
                className={classes.professionContainer}
                error={isErrorFormField('maritalStatus')}
              >
                <Select
                  id='maritalStatus'
                  name='maritalStatus'
                  placeholder='Marital Status *'
                  displayEmpty
                  className={classes.inputTextField}
                  value={proposerDetails?.maritalStatus ?? ''}
                  onChange={handleProposerDetailsChange}
                  sx={{
                    '&  .MuiSelect-select': {
                      height: '0.7rem !important',
                      minHeight: 'unset',
                    },
                    '& .MuiSelect-iconOutlined': {
                      display: proposerDetails?.maritalStatus ? 'none' : '',
                    },
                  }}
                  endAdornment={
                    <IconButton
                      id='maritalStatusClear'
                      sx={{
                        display: proposerDetails?.maritalStatus ? '' : 'none',
                      }}
                      className={'clear-button'}
                      onClick={() => handleClear('maritalStatus')}
                    >
                      <Clear />
                    </IconButton>
                  }
                  onOpen={() => {
                    removeScroll()
                  }}
                  onClose={() => {
                    addScroll()
                  }}
                >
                  <MenuItem value={''} className={'default-menu-item'}>
                    Marital Status
                  </MenuItem>
                  {providerDetails?.maritalStatus?.map(
                    (maritalStatusListItem) => (
                      <MenuItem
                        value={maritalStatusListItem?.value}
                        key={maritalStatusListItem?.value}
                      >
                        {maritalStatusListItem?.label}
                      </MenuItem>
                    )
                  )}
                </Select>
                {isErrorFormField('maritalStatus') && (
                  <FormHelperText>Invalid Marital Status</FormHelperText>
                )}
              </FormControl>
            </div>

            <div className={classes.inputContent}>
              <TextField
                id='panNumber'
                name='panNumber'
                type='text'
                placeholder='PAN Number *'
                required
                error={isErrorFormField('panNumber')}
                helperText={
                  isErrorFormField('panNumber') ? 'Invalid PAN number' : ''
                }
                className={`${classes.inputTextField} ${classes.setFieldHeight}`}
                value={proposerDetails?.panNumber ?? ''}
                onChange={handleProposerDetailsChange}
              ></TextField>
            </div>

            <div className={classes.inputContent}>
              <TextField
                id='aadharNumber'
                name='aadharNumber'
                type='text'
                placeholder='Aadhar Number'
                error={isErrorFormField('aadharNumber')}
                helperText={
                  isErrorFormField('aadharNumber')
                    ? 'Invalid Aadhar Number'
                    : ''
                }
                className={`${classes.inputTextField} ${classes.setFieldHeight}`}
                value={proposerDetails?.aadharNumber ?? ''}
                onChange={handleProposerDetailsChange}
                required
              ></TextField>
            </div>
          </div>

          <div className={classes.inputContainer}>
            <div className={classes.inputContentKyc}>
              <ul className={classes.listItems}>
                <li>
                  <span className={classes.itemsDescription}>
                    KYC is mandatory as per recent IRDAI guidelines
                  </span>
                </li>
              </ul>
              <span className={classes.kycDescription}>
                KYC documents need to be shared post policy purchase.
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className={classes.backgroundContainer}>
        <div className={classes.container}>
          <div className={classes.subHeadingContainer}>
            <div className={classes.subHeadingContent}>
              <span className={classes.subHeading}>Current Address</span>
            </div>
            <div>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      id='checkBox'
                      size='small'
                      checked={isPermanentAddress}
                      onChange={handleAddressCheck}
                    />
                  }
                  label='Permanent Address'
                  className={classes.subHeading}
                />
              </FormGroup>
            </div>
          </div>
          <div className={classes.inputContainer}>
            <div className={classes.addressLineContainer}>
              <TextField
                id='addressLine'
                name='addressLine'
                type='text'
                required
                error={isErrorFormField('addressLine', 'currentAddress')}
                helperText={
                  isErrorFormField('addressLine', 'currentAddress')
                    ? 'Invalid Flat/House no, Street'
                    : ''
                }
                placeholder='Flat/House no, Street *'
                value={currentAddress?.addressLine}
                onChange={(event) =>
                  handleProposerAddressChange(event, 'currentAddress')
                }
                className={`${classes.inputTextField} ${classes.setFieldHeight}`}
              ></TextField>
            </div>

            <div className={classes.inputContent}>
              <TextField
                id='landmark'
                type='text'
                name='landmark'
                placeholder='Landmark'
                error={isErrorFormField('landmark', 'currentAddress')}
                helperText={
                  isErrorFormField('landmark', 'currentAddress')
                    ? 'Invalid Landmark'
                    : ''
                }
                value={currentAddress?.landmark}
                onChange={(event) =>
                  handleProposerAddressChange(event, 'currentAddress')
                }
                className={`${classes.inputTextField} ${classes.setFieldHeight}`}
              ></TextField>
            </div>
          </div>

          <div className={classes.inputContainer}>
            <div className={classes.inputContent}>
              <TextField
                id='pincode'
                type='number'
                name='pincode'
                placeholder='Pincode *'
                required
                error={isErrorFormField('pincode', 'currentAddress')}
                helperText={
                  isErrorFormField('pincode', 'currentAddress')
                    ? 'Invalid Pincode'
                    : ''
                }
                value={currentAddress?.pincode}
                onChange={async (event) => {
                  event.target.value = Math.max(
                    0,
                    parseInt(event?.target?.value)
                  )
                    ?.toString()
                    ?.slice(0, 6)
                  handleProposerAddressChange(event, 'currentAddress')
                  if (event?.target?.value?.length === 6) {
                    await fetchFullAddress(
                      event?.target?.value,
                      'currentAddress'
                    )
                  }
                }}
                className={`${classes.inputTextField} ${classes.setFieldHeight}`}
              ></TextField>
            </div>
            <div className={classes.inputContent}>
              <TextField
                id='city'
                type='text'
                name='city'
                placeholder='City *'
                required
                error={isErrorFormField('city', 'currentAddress')}
                helperText={
                  isErrorFormField('city', 'currentAddress')
                    ? 'Invalid City'
                    : ''
                }
                value={currentAddress?.city}
                onChange={(event) =>
                  handleProposerAddressChange(event, 'currentAddress')
                }
                className={`${classes.inputTextField} ${classes.setFieldHeight}`}
              ></TextField>
            </div>
            <div className={classes.inputContent}>
              <TextField
                id='state'
                type='text'
                name='state'
                placeholder='State *'
                required
                error={isErrorFormField('state', 'currentAddress')}
                helperText={
                  isErrorFormField('state', 'currentAddress')
                    ? 'Invalid State'
                    : ''
                }
                value={currentAddress?.state}
                onChange={(event) =>
                  handleProposerAddressChange(event, 'currentAddress')
                }
                className={`${classes.inputTextField} ${classes.setFieldHeight}`}
              ></TextField>
            </div>
          </div>
        </div>
      </div>

      {!isPermanentAddress && (
        <div className={classes.backgroundContainer}>
          <div className={classes.container}>
            <div className={classes.subHeadingContainer}>
              <span className={classes.subHeading}>Permanent Address</span>
            </div>

            <div className={classes.inputContainer}>
              <div className={classes.addressLineContainer}>
                <TextField
                  id='addressLine'
                  name='addressLine'
                  type='text'
                  placeholder='Flat/House no, Street *'
                  required
                  error={isErrorFormField('addressLine', 'permanentAddress')}
                  helperText={
                    isErrorFormField('addressLine', 'permanentAddress')
                      ? 'Invalid Flat/House no, Street'
                      : ''
                  }
                  value={permanentAddress?.addressLine}
                  onChange={(event) =>
                    handleProposerAddressChange(event, 'permanentAddress')
                  }
                  className={`${classes.inputTextField} ${classes.setFieldHeight}`}
                ></TextField>
              </div>

              <div className={classes.inputContent}>
                <TextField
                  id='landmark'
                  type='text'
                  name='landmark'
                  placeholder='Landmark'
                  error={isErrorFormField('landmark', 'permanentAddress')}
                  helperText={
                    isErrorFormField('landmark', 'permanentAddress')
                      ? 'Invalid Landmark'
                      : ''
                  }
                  value={permanentAddress?.landmark}
                  onChange={(event) =>
                    handleProposerAddressChange(event, 'permanentAddress')
                  }
                  className={`${classes.inputTextField} ${classes.setFieldHeight}`}
                ></TextField>
              </div>
            </div>

            <div className={classes.inputContainer}>
              <div className={classes.inputContent}>
                <TextField
                  id='pincodePermanent'
                  type='number'
                  name='pincode'
                  placeholder='Pincode *'
                  required
                  error={isErrorFormField('pincode', 'permanentAddress')}
                  helperText={
                    isErrorFormField('pincode', 'permanentAddress')
                      ? 'Invalid Pincode'
                      : ''
                  }
                  value={permanentAddress?.pincode}
                  onChange={async (event) => {
                    event.target.value = Math.max(
                      0,
                      parseInt(event?.target?.value)
                    )
                      ?.toString()
                      ?.slice(0, 6)
                    handleProposerAddressChange(event, 'permanentAddress')
                    if (event?.target?.value?.length === 6) {
                      await fetchFullAddress(
                        event?.target?.value,
                        'permanentAddress'
                      )
                    }
                  }}
                  className={`${classes.inputTextField} ${classes.setFieldHeight}`}
                ></TextField>
              </div>
              <div className={classes.inputContent}>
                <TextField
                  id='city'
                  type='text'
                  name='city'
                  placeholder='City *'
                  required
                  error={isErrorFormField('city', 'permanentAddress')}
                  helperText={
                    isErrorFormField('city', 'permanentAddress')
                      ? 'Invalid City'
                      : ''
                  }
                  value={permanentAddress?.city}
                  onChange={(event) =>
                    handleProposerAddressChange(event, 'permanentAddress')
                  }
                  className={`${classes.inputTextField} ${classes.setFieldHeight}`}
                ></TextField>
              </div>
              <div className={classes.inputContent}>
                <TextField
                  id='state'
                  type='text'
                  name='state'
                  placeholder='State *'
                  required
                  error={isErrorFormField('state', 'permanentAddress')}
                  helperText={
                    isErrorFormField('state', 'permanentAddress')
                      ? 'Invalid State'
                      : ''
                  }
                  value={permanentAddress?.state}
                  onChange={(event) =>
                    handleProposerAddressChange(event, 'permanentAddress')
                  }
                  className={`${classes.inputTextField} ${classes.setFieldHeight}`}
                ></TextField>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default React.memo(ProposerDetailsForm)

const useStyles = makeStyles({
  backgroundContainer: {
    background: 'linear-gradient(to bottom, #4DC3E3 , #FFFFFF )',
    padding: '1px',
    borderRadius: '11px',
    marginBottom: '20px',
  },
  firstNameField: {
    '&  .MuiOutlinedInput-input': {
      height: '0.7rem',
    },
  },
  container: {
    padding: '25px',
    borderRadius: '10px',
    // boxShadow: '0px 8px 8px 0px #0000001A',
    background: COLORS.WHITE,
  },
  professionContainer: { width: '100%', margin: 0 },
  subHeadingContainer: {
    marginBottom: '15px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  subHeadingContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  subHeading: {
    fontSize: '16px',
    color: '#29AACD',
    fontWeight: '600',
  },
  listItems: {
    color: '#838383',
    marginLeft: '-20px',
    margin: '0',
  },
  inputContainer: {
    display: 'flex',
    marginBottom: '20px',
    gap: '25px',
  },
  inputContent: {
    width: 'calc(100% / 3)',
  },
  inputContentKyc: {
    width: '100%',
  },
  inputItem: {
    display: 'flex',
    gap: '5px',
  },
  heightWeightContainer: {
    display: 'flex',
    gap: '22px',
  },
  heightWeightContent: {
    borderRadius: '5px !important',
    backgroundColor: 'white !important',
    fontSize: '12px !important',
    fontFamily: 'Roboto !important',
  },
  setFieldHeight: {
    '&  .MuiOutlinedInput-input': {
      height: '0.7rem',
    },
  },
  itemsDescription: {
    fontSize: '11px',
    fontStyle: 'italic',
    color: '#838383',
  },
  kycDescription: {
    fontSize: '11px',
    fontStyle: 'italic',
    color: '#EF669B',
  },
  addressLineContainer: {
    width: 'calc(2*(105% / 3))',
  },
  genterRadio: {
    '& .MuiFormControlLabel-label': {
      fontSize: '12px',
    },
  },
  inputTextField: {
    borderRadius: '5px !important',
    backgroundColor: 'white !important',
    fontSize: '12px !important',
    fontFamily: 'Roboto !important',
    width: '100%',
  },
  inputFieldError: {
    fontSize: '11px !important',
    color: 'red',
    marginTop: '-10px',
  },
  emptyField: {
    border: '1px solid red !important',
  },
})
